import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createApiClient, currentTenantId } from '../api.js';
import { useAuth } from '@clerk/clerk-react';
import { CopyIcon, XIcon } from '../components/icons';

/**
 * Copy-tour-to-another-project button + popover.
 *
 * Fetches the tenant's projects on demand, lets the user pick the
 * target, optionally rename the clone, and POSTs to the backend's
 * copy endpoint.  On success we navigate to the new tour's page on
 * the target project so the user sees the result immediately.
 *
 * Doesn't use a modal lib — a small absolutely-positioned panel
 * keyed off the button's position is enough for the one action.
 */
export function CopyTourButton({ tour, api }) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsErr, setProjectsErr] = useState(null);
  const [target, setTarget] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Projects list must be fetched without the current project scope
  // so we actually see all of them — the page's existing client is
  // project-scoped and GET /v1/admin/projects ignores the header
  // anyway, but we re-create an unscoped client so the intent is
  // explicit.
  useEffect(() => {
    if (!open) return undefined;
    const unscoped = createApiClient(getToken, { tenantId: currentTenantId() });
    unscoped('/v1/admin/projects')
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.items || []);
        setProjects(list.filter((p) => p.project_id !== tour.project_id));
        setTarget(list.find((p) => p.project_id !== tour.project_id)?.project_id || '');
      })
      .catch((e) => setProjectsErr(e.message));
    setTitle(`${tour.title} (copy)`);
    return undefined;
  }, [open, getToken, tour.project_id, tour.title]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const copy = await api(`/v1/admin/tours/${encodeURIComponent(tour.tour_id)}/copy`, {
        method: 'POST',
        body: JSON.stringify({
          target_project_id: target,
          title: title.trim(),
        }),
      });
      const targetSlug = projects.find((p) => p.project_id === target)?.slug;
      if (targetSlug && copy?.tour_id) {
        navigate(`/projects/${targetSlug}/tours/${encodeURIComponent(copy.tour_id)}`);
      } else {
        setOpen(false);
      }
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="copy-tour" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="btn btn--secondary"
        onClick={() => setOpen((o) => !o)}
      >
        <CopyIcon /> Copy to project…
      </button>
      {open && (
        <form className="copy-tour__panel" onSubmit={submit}>
          <div className="copy-tour__title">
            Copy <strong>{tour.title}</strong> to another project.
            The clone gets a new tour id, resets to draft v1, and
            keeps its URL patterns + steps verbatim.
          </div>
          {projectsErr && (
            <p style={{ color: 'var(--danger-700)', fontSize: 12 }}>
              Couldn't load projects — {projectsErr}
            </p>
          )}
          {!projectsErr && projects.length === 0 && (
            <p className="list__meta">
              No other projects on this tenant. Create one from the
              Projects page first.
            </p>
          )}
          {projects.length > 0 && (
            <>
              <label className="field">
                <span className="field__label">Target project</span>
                <select
                  className="select"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  required
                >
                  {projects.map((p) => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.name} · <code>{p.slug}</code>
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field__label">Clone title</span>
                <input
                  className="input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`${tour.title} (copy)`}
                />
              </label>
              {err && (
                <p style={{ color: 'var(--danger-700)', fontSize: 12, margin: 0 }}>{err}</p>
              )}
              <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={busy}
                  onClick={() => { setOpen(false); setErr(null); }}
                >
                  <XIcon /> Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={busy || !target}
                >
                  <CopyIcon /> {busy ? 'Copying…' : 'Copy'}
                </button>
              </div>
            </>
          )}
        </form>
      )}
      <style>{COPY_TOUR_CSS}</style>
    </span>
  );
}

const COPY_TOUR_CSS = `
  .copy-tour {
    position: relative;
    display: inline-flex;
  }
  .copy-tour__panel {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 20;
    width: 320px;
    padding: 14px;
    border-radius: var(--r-md);
    background: var(--surface-0);
    border: 1px solid var(--border-1);
    box-shadow: var(--shadow-2);
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .copy-tour__title {
    font-size: 13px;
    color: var(--text-2);
  }
`;
